import { AcceptedRecruiterItem } from './AcceptedRecruiterItem';
import {
  type Dispatch,
  type HTMLAttributes,
  type SetStateAction,
  useCallback,
  useMemo,
} from 'react';
import { IconType } from '@src/IconType';
import { Indicator } from '@components/base/Indicator';
import { InvitedRecruiterItem } from './InvitedRecruiterItem';
import { type OrganizationRole } from '@funnellab/shared/src/types/directus-enums';
import { RecruiterInviteInput } from '@components/jobDetails/recruiter/RecruiterInviteInput';
import { RecruiterListContainer } from './RecruiterListContainer';
import { Tabs } from '@src/components/Tabs';
import { classNames } from '@src/utils/classNames';
import { useTranslation } from 'react-i18next';
import recruiterService, {
  type AcceptedRecruiter,
  type Recruiter,
} from '@src/api/services/RecruiterService';

export type RecruiterViewProps = {
  recruiter: Recruiter[];
  setRecruiter?: Dispatch<SetStateAction<Recruiter[]>>;
  allRecruiters: Recruiter[];
  onRecruiterAdded?: (newRecruiter: Recruiter) => void;
  organizationId?: string;
  onRoleChange?: (
    recruiter: AcceptedRecruiter,
    role: `${OrganizationRole}`,
  ) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
} & HTMLAttributes<HTMLDivElement>;

export const RecruiterView = ({
  recruiter,
  setRecruiter,
  allRecruiters,
  organizationId,
  onRecruiterAdded,
  onRoleChange,
  onDelete,
  ...rest
}: RecruiterViewProps) => {
  const { t } = useTranslation('recruiterView');

  const handleDelete = useCallback(
    (id: string) => {
      setRecruiter?.((prev) => prev.filter((p) => p.id !== id));
      return onDelete ? onDelete(id) : Promise.resolve();
    },
    [setRecruiter, onDelete],
  );

  const invitedRecruiters = useMemo(
    () => recruiter.filter((r) => !r.accepted),
    [recruiter],
  );

  const acceptedRecruiters = useMemo(
    () => recruiter.filter((r) => r.accepted),
    [recruiter],
  );

  const resendInvite = useCallback(
    (id: Recruiter['id']) => {
      if (!organizationId) {
        throw new Error('Organization Id is missing');
      }
      return recruiterService.resendInvite(organizationId, id);
    },
    [organizationId],
  );

  return (
    <div
      {...rest}
      className={classNames(
        'flex flex-col gap-4 sm:min-w-lg md:min-w-150',
        rest.className,
      )}
    >
      <RecruiterInviteInput
        setRecruiter={setRecruiter}
        recruiter={recruiter}
        allRecruiter={allRecruiters}
        onRecruiterAdded={onRecruiterAdded}
        organizationId={organizationId}
      />
      <Tabs
        variant="underline"
        tabs={[
          {
            title: (
              <span className="flex gap-1 items-center">
                <span>{t('tabHeaderAccepted')}</span>
                <Indicator count={acceptedRecruiters.length} />
              </span>
            ),
            id: 'members',
            IconLeading: IconType.Users,
            default: true,
            content: (
              <RecruiterListContainer isEmpty={acceptedRecruiters.length === 0}>
                {acceptedRecruiters.map((r) => (
                  <AcceptedRecruiterItem
                    key={`accepted-recruiter-${r.id}`}
                    recruiter={r}
                    allRecruiter={recruiter}
                    onDelete={() => handleDelete(r.id)}
                    onRoleChange={
                      onRoleChange ? (role) => onRoleChange(r, role) : undefined
                    }
                  />
                ))}
              </RecruiterListContainer>
            ),
          },
          {
            title: (
              <span className="flex gap-1 items-center">
                <span>{t('tabHeaderInvited')}</span>
                <Indicator count={invitedRecruiters.length} />
              </span>
            ),
            id: 'requests',
            IconLeading: IconType.UserPlus,
            content: (
              <RecruiterListContainer isEmpty={invitedRecruiters.length === 0}>
                {invitedRecruiters.map((r) => (
                  <InvitedRecruiterItem
                    recruiter={r}
                    key={`invited-recruiter-${r.id}`}
                    onDelete={() => handleDelete(r.id)}
                    onResendInvite={
                      organizationId ? () => resendInvite(r.id) : undefined
                    }
                  />
                ))}
              </RecruiterListContainer>
            ),
          },
        ]}
      ></Tabs>
    </div>
  );
};
